import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { listForks } from "../../../api/threads/threadsApi";
import { appPaths } from "../../../shared/routing/appPaths";
import { formatDateTime } from "../../../shared/formatting/dateTimes";
import { useLoad } from "../../../shared/loading/useLoad";
import { useTabTitle, WorkspaceLink } from "../../../shell/workspace";
import { formatThreadEntryRef, parseThreadEntryRef } from "../../../shared/references/threadReferences";
import IconButton from "../../../ui/buttons/IconButton";
import CopyReference from "../../../ui/content/CopyReference";
import RenameThreadDialog from "../hosting/RenameThreadDialog";
import HostingLine from "../hosting/HostingLine";
import RunGraphButton from "../runGraph/RunGraphButton";
import { ThreadDefaultRunsWith } from "../llmChoice/RunsWithCard";
import ThreadStatusBadge from "../status/ThreadStatusBadge";
import type { ThreadDisplayStatus } from "../status/threadStatus";
import ThreadSubjectLink from "./ThreadSubjectLink";
import { useThreadContext } from "./threadPageContext";
import { allows } from "./viewerActions";

interface ThreadHeaderProps {
  status: ThreadDisplayStatus;
  lastEntryNumber: number;
  actions: ReactNode;
}

/**
 * Title and status; then one row (F67): where it runs, its reference, its Workflow and what
 * can be done with it; below, where the Thread came from and what forked from it.
 */
export default function ThreadHeader({ status, lastEntryNumber, actions }: ThreadHeaderProps) {
  const { t, i18n } = useTranslation();
  const { threadId, overview, viewerIsHost, nameOf } = useThreadContext();
  const waiting = overview.projection.open_human_step;
  const imported = overview.importedExample;
  const parent = overview.parent === null ? null : parseThreadEntryRef(overview.parent);
  useTabTitle({ kind: "thread", name: overview.title });

  return (
    <header className="space-y-2">
      <div className="flex flex-wrap items-start gap-2">
        <h2 className="app-title min-w-0 flex-1 text-xl font-bold">{overview.title}</h2>
        {allows(overview.viewer, "rename") ? <RenameButton lastEntryNumber={lastEntryNumber} /> : null}
        {/* Its connection the hosting line says in its own words (F55). */}
        <ThreadStatusBadge status={status} waiting={waiting ?? null} hostName={viewerIsHost ? null : nameOf(overview.host)} />
      </div>
      {overview.bindings.subject === null ? null : <ThreadSubjectLink subject={overview.bindings.subject} />}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <HostingLine lastEntryNumber={lastEntryNumber} />
        <CopyReference value={formatThreadEntryRef({ threadId, number: lastEntryNumber })} label={t("threads.header.referenceHint")} />
        <WorkflowLink />
        {/* The Run's own graph, beside the Workflow it follows (F149, F131). */}
        {overview.workflow === null ? null : <RunGraphButton threadId={threadId} />}
        <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div>
      </div>
      {/* Where it runs is the hosting line's; with what, the Thread's "Runs with" (W-07). */}
      {overview.state === "closed" ? null : <ThreadDefaultRunsWith />}
      {imported === null ? null : (
        <p className="app-text-muted text-xs" role="note">
          {t("threads.header.importedExample", {
            date: formatDateTime(imported.recordedAt, i18n.resolvedLanguage),
            model: imported.model,
          })}
        </p>
      )}
      {parent === null ? null : (
        <p className="app-text-muted text-xs">
          {t("threads.header.forkOf")}{" "}
          <Link to={appPaths.thread(parent.threadId, parent.number)} className="app-link app-link-cue-away" title={overview.parent ?? undefined}>
            {t("threads.header.forkOfEntry", { number: parent.number })}
          </Link>
        </p>
      )}
      <ForkLinks threadId={threadId} />
    </header>
  );
}

/** Rename the Thread (F68): its host, for everyone in it. */
function RenameButton({ lastEntryNumber }: { lastEntryNumber: number }) {
  const { t } = useTranslation();
  const { threadId, overview } = useThreadContext();
  const [renaming, setRenaming] = useState(false);
  return (
    <>
      <IconButton label={t("threads.rename.title")} title={t("threads.rename.title")} onClick={() => { setRenaming(true); }} className="h-7 w-7 p-1">
        <PencilSquareIcon className="h-4 w-4" />
      </IconButton>
      {renaming ? <RenameThreadDialog threadId={threadId} title={overview.title} lastEntryNumber={lastEntryNumber} onClose={() => { setRenaming(false); }} /> : null}
    </>
  );
}

/** The Thread's Workflow, opened beside it: where its steps are edited and saved (F67). */
function WorkflowLink() {
  const { t } = useTranslation();
  const { overview, workflowName } = useThreadContext();
  const workflow = overview.workflow;
  if (workflow === null) return null;
  return (
    <WorkspaceLink route={appPaths.workflow(workflow)} placement="beside" className="app-link app-link-cue-away text-xs" title={workflow}>
      {workflowName === null ? t("threads.workflow.unnamed") : t("threads.header.workflow", { name: workflowName })}
    </WorkspaceLink>
  );
}

function ForkLinks({ threadId }: { threadId: string }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => listForks(threadId, signal), [threadId]);
  const { state } = useLoad(load);
  if (state.status !== "loaded" || state.value.length === 0) {
    return null;
  }
  return (
    <p className="app-text-muted flex flex-wrap gap-x-2 text-xs">
      <span>{t("threads.header.forks", { count: state.value.length })}</span>
      {state.value.map((fork) => (
        <Link key={fork.threadId} to={appPaths.thread(fork.threadId)} className="app-link app-link-cue-away">
          {fork.title}
          {fork.parent === null ? null : ` (#${String(parseThreadEntryRef(fork.parent)?.number ?? "")})`}
        </Link>
      ))}
    </p>
  );
}
